/**
 * AuditLogSection (TRA-1626): who looked at or changed this team's data.
 * For the team owner and for TrainLoop staff, impersonating the team or
 * a member of it (the registry's `access: 'owner-or-employee'`); the API
 * enforces the same rule and records the staff read.
 *
 * A table of the newest events under four filters (who, action, result,
 * how far back), paged by the API's cursor at 50, 100 or 200 rows a
 * page, with how many rows match and where the reader is; a row opens
 * the detail sheet with the before and after; Download CSV exports the
 * current filters. The actions the filter offers are the ones this
 * team's log holds.
 */

import { Download, ScrollText } from 'lucide-react'
import { useReducer, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDebounce } from '@/hooks/useDebounce'
import { type AuditEvent, downloadAuditCsv, useAuditEvents } from '@/hooks/useAuditEvents'

import { ActorCell } from './ActorCell'
import { AuditEventSheet } from './AuditEventSheet'
import {
  actionLabel,
  formatWhen,
  OUTCOME_LABELS,
  outcomeVariant,
  resourceLabel,
  TIME_RANGE_LABELS,
  TIME_RANGES,
  type TimeRange,
} from './auditLabels'
import {
  ANY,
  auditParams,
  type Filters,
  INITIAL_PAGING,
  OUTCOMES,
  PAGE_SIZES,
  pagePosition,
  type Paging,
  type PagingAction,
  pagingReducer,
  toPageSize,
} from './auditQuery'

function EventRow({ event, onOpen }: { event: AuditEvent; onOpen: (event: AuditEvent) => void }): React.JSX.Element {
  return (
    <TableRow
      tabIndex={0}
      aria-label={`Open event: ${actionLabel(event.action)}`}
      className="cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => {
        onOpen(event)
      }}
      onKeyDown={(keyboard) => {
        if (keyboard.key === 'Enter' || keyboard.key === ' ') {
          keyboard.preventDefault()
          onOpen(event)
        }
      }}
    >
      <TableCell className="whitespace-nowrap text-sm">{formatWhen(event.occurred_at)}</TableCell>
      <TableCell>
        <ActorCell event={event} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">{actionLabel(event.action)}</TableCell>
      <TableCell className="max-w-[20rem] truncate font-mono text-xs text-muted-foreground">
        {resourceLabel(event) === '' ? '—' : resourceLabel(event)}
      </TableCell>
      <TableCell>
        <Badge variant={outcomeVariant(event.outcome)}>{OUTCOME_LABELS[event.outcome]}</Badge>
      </TableCell>
    </TableRow>
  )
}

function FilterField({ id, label, children }: { id: string; label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={EYEBROW}>
        {label}
      </Label>
      {children}
    </div>
  )
}

interface PagerProps {
  paging: Paging
  dispatch: (action: PagingAction) => void
  /** Rows on this page. */
  shown: number
  total: number
  totalCapped: boolean
  nextCursor: string | null
  isFetching: boolean
}

function PageSizeSelect({ pageSize, dispatch }: { pageSize: number; dispatch: (action: PagingAction) => void }): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="audit-page-size" className={EYEBROW}>
        Rows per page
      </Label>
      <Select
        value={String(pageSize)}
        onValueChange={(value) => {
          const next = toPageSize(value)
          if (next !== null) dispatch({ type: 'pageSize', pageSize: next })
        }}
      >
        <SelectTrigger id="audit-page-size" className="h-8 w-20 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAGE_SIZES.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function PageButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <Button variant="outline" size="sm" disabled={disabled} onClick={onClick}>
      {label}
    </Button>
  )
}

function PageButtons({ page, dispatch, total, totalCapped, nextCursor, isFetching }: Omit<PagerProps, 'paging' | 'shown'> & { page: number }): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <PageButton label="Back to newest" disabled={page === 0 || isFetching} onClick={() => { dispatch({ type: 'newest' }) }} />
      <PageButton label="Newer" disabled={page === 0 || isFetching} onClick={() => { dispatch({ type: 'newer' }) }} />
      <PageButton
        label="Older"
        disabled={nextCursor === null || isFetching}
        onClick={() => {
          if (nextCursor !== null) dispatch({ type: 'older', cursor: nextCursor, total: { total, capped: totalCapped } })
        }}
      />
    </div>
  )
}

function Pager({ paging, dispatch, shown, total, totalCapped, nextCursor, isFetching }: PagerProps): React.JSX.Element {
  const page = paging.cursors.length
  const counted = paging.firstTotal ?? { total, capped: totalCapped }
  const position = pagePosition(page, paging.pageSize, shown, counted.total, counted.capped)
  // While the next page loads the last one stays on screen, and the
  // paging state already names the next page: the position would
  // describe rows that are not shown yet.
  return (
    <nav aria-label="Audit log pages" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
      <span aria-live="polite">{isFetching ? 'Loading.' : `${position.rows}. ${position.pages}.`}</span>
      <div className="flex flex-wrap items-center gap-3">
        <PageSizeSelect pageSize={paging.pageSize} dispatch={dispatch} />
        <PageButtons page={page} dispatch={dispatch} total={total} totalCapped={totalCapped} nextCursor={nextCursor} isFetching={isFetching} />
      </div>
    </nav>
  )
}

export function AuditLogSection({ section }: SettingsSectionProps): React.JSX.Element {
  const [paging, dispatch] = useReducer(pagingReducer, INITIAL_PAGING)
  const { filters, cursors } = paging
  const [open, setOpen] = useState<AuditEvent | null>(null)
  const [downloading, setDownloading] = useState(false)
  // The range counts back from when the page opened: a bound that moved
  // with the clock would make every refetch a different query.
  const [since] = useState(() => new Date())
  const actor = useDebounce(filters.actor, 300)
  const cursor = cursors.length === 0 ? null : cursors[cursors.length - 1]
  const params = auditParams({ ...filters, actor }, cursor ?? null, since, paging.pageSize)
  const { data, isLoading, isFetching, isError, error } = useAuditEvents(params)
  const scrollRef = useRef<HTMLDivElement>(null)

  const update = (patch: Partial<Filters>): void => {
    dispatch({ type: 'filter', patch })
  }

  const download = async (): Promise<void> => {
    setDownloading(true)
    try {
      const { cursor: _cursor, limit: _limit, ...exportParams } = auditParams(
        { ...filters, actor },
        null,
        since,
        paging.pageSize,
      )
      const result = await downloadAuditCsv({ ...exportParams, format: 'csv' })
      if (result.truncated) {
        toast.warning(
          result.maxRows === null
            ? 'The file holds the newest rows only. Narrow the time range for the rest.'
            : `The file holds the newest ${result.maxRows.toLocaleString('en-US')} rows. Narrow the time range for the rest.`,
        )
      } else {
        toast.success('Audit log downloaded')
      }
    } catch (failure) {
      toast.error(getApiErrorDetail(failure) ?? 'The audit log could not be downloaded.')
    } finally {
      setDownloading(false)
    }
  }

  const items = data?.items ?? []
  const actions = data?.actions ?? []
  const filtered = params.actor !== undefined || params.action !== undefined || params.outcome !== undefined

  return (
    <div>
      <SettingsPageHeader
        section={section}
        action={
          <Button
            size="sm"
            variant="outline"
            disabled={downloading || isLoading || isError}
            onClick={() => {
              void download()
            }}
          >
            <Download />
            {downloading ? 'Preparing...' : 'Download CSV'}
          </Button>
        }
      />

      <div className="grid gap-4 border-t border-border py-6 md:grid-cols-4">
        <FilterField id="audit-actor" label="Who">
          <Input
            id="audit-actor"
            value={filters.actor}
            placeholder="Email or id"
            className="h-8 text-xs"
            onChange={(change) => {
              update({ actor: change.target.value })
            }}
          />
        </FilterField>
        <FilterField id="audit-action" label="Action">
          <Select
            value={filters.action}
            onValueChange={(value) => {
              update({ action: value })
            }}
          >
            <SelectTrigger id="audit-action" className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any action</SelectItem>
              {actions.map((action) => (
                <SelectItem key={action} value={action}>
                  {actionLabel(action)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField id="audit-outcome" label="Result">
          <Select
            value={filters.outcome}
            onValueChange={(value) => {
              update({ outcome: value })
            }}
          >
            <SelectTrigger id="audit-outcome" className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any result</SelectItem>
              {OUTCOMES.map((outcome) => (
                <SelectItem key={outcome} value={outcome}>
                  {OUTCOME_LABELS[outcome]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField id="audit-range" label="When">
          <Select
            value={filters.range}
            onValueChange={(value) => {
              update({ range: value as TimeRange })
            }}
          >
            <SelectTrigger id="audit-range" className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((range) => (
                <SelectItem key={range} value={range}>
                  {TIME_RANGE_LABELS[range]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive" role="alert">
          {getApiErrorDetail(error) ?? 'The audit log could not be loaded.'}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={filtered || cursor !== null ? 'No events match these filters' : 'Nothing recorded yet'}
          description={
            filtered || cursor !== null
              ? 'Widen the time range or clear a filter.'
              : 'When someone opens a trace, saves a review, changes a model or a prompt, invites a member or creates an API key, it appears here. TrainLoop staff viewing this team are listed too.'
          }
        />
      ) : (
        <>
          <div className="relative border border-border">
            <SideScrollArrows scrollRef={scrollRef} />
            <Table containerRef={scrollRef}>
              <TableHeader>
                <TableRow>
                  <TableHead>When (newest first)</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((event) => (
                  <EventRow key={event.id} event={event} onOpen={setOpen} />
                ))}
              </TableBody>
            </Table>
          </div>
          {data === undefined ? null : (
            <Pager
              paging={paging}
              dispatch={dispatch}
              shown={items.length}
              total={data.total}
              totalCapped={data.total_capped}
              nextCursor={data.next_cursor}
              isFetching={isFetching}
            />
          )}
        </>
      )}

      <AuditEventSheet
        event={open}
        onClose={() => {
          setOpen(null)
        }}
      />
    </div>
  )
}
